import React from 'react';
import { surface } from '../ui/surface';

export const schoolSurface = surface;

export const StatusDot = ({ tone = 'muted', label, size = 'sm' }) => {
  const dot =
    tone === 'ok'
      ? 'bg-positive'
      : tone === 'critical'
        ? 'bg-critical'
        : tone === 'warning'
          ? 'bg-warning'
          : 'bg-muted-500';
  const sizing =
    size === 'lg'
      ? { row: 'gap-2 text-base', mark: 'h-2.5 w-2.5' }
      : { row: 'gap-1.5 text-xs', mark: 'h-1.5 w-1.5' };

  return (
    <span className={`inline-flex shrink-0 items-center text-muted-300 ${sizing.row}`}>
      <span className={`rounded-full ${sizing.mark} ${dot}`} aria-hidden />
      {label}
    </span>
  );
};

export const GhostLink = ({ children, className = '', ...props }) => (
  <button
    type="button"
    className={`inline-flex items-center gap-1.5 text-sm text-muted-400 transition hover:text-white ${className}`}
    {...props}
  >
    {children}
  </button>
);

export const NavPageLink = ({ icon, children, className = '', ...props }) => (
  <GhostLink className={`text-xs ${className}`} {...props}>
    {icon && <span className="material-icons text-[14px]">{icon}</span>}
    {children}
    <span className="material-icons text-[13px]" aria-hidden>
      open_in_new
    </span>
  </GhostLink>
);

export const AddDeviceLink = ({ onClick, label = 'Добавить устройство', className = '' }) => (
  <GhostLink className={className} onClick={onClick}>
    <span className="material-icons text-sm">add</span>
    {label}
  </GhostLink>
);

export const EquipmentEmpty = ({ hint, onAdd, canManage, addLabel }) => (
  <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
    <p className="max-w-lg text-sm text-muted-400">{hint}</p>
    {canManage && onAdd && (
      <AddDeviceLink className="mt-2" onClick={onAdd} label={addLabel} />
    )}
  </div>
);

export const SectionFrame = ({ eyebrow, title, subtitle, actions, navLinks, children, className = '' }) => (
  <section className={className}>
    <div className={`overflow-hidden rounded-3xl ${schoolSurface.section}`}>
      <header className="flex flex-col gap-3 px-6 pt-6 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          {eyebrow && (
            <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-500">
              {eyebrow}
            </p>
          )}
          {title && <h3 className="mt-1 font-display text-xl text-white md:text-2xl">{title}</h3>}
          {subtitle && <p className="mt-1 text-sm text-muted-400">{subtitle}</p>}
        </div>
        {(actions || navLinks) && (
          <div className="flex shrink-0 flex-col items-end gap-1.5">
            {actions}
            {navLinks}
          </div>
        )}
      </header>
      <div className="px-6 pb-6 pt-6">{children}</div>
    </div>
  </section>
);

const ACCENT_BAR = {
  critical: 'bg-critical',
  ok: 'bg-positive',
};

/** Полоска слева: доступен — зелёная, недоступен — красная. Остальные состояния без акцента. */
export const availabilityAccent = (tone) => (tone === 'ok' || tone === 'critical' ? tone : false);

export const DeviceRowCard = ({
  icon,
  iconClass = 'text-brand-100',
  name,
  address,
  extra,
  status,
  accent = false,
  onOpen,
  hoverActions,
  reserveActions = false,
  children,
}) => {
  const accentTone = accent === true ? 'critical' : accent;
  const accentClass = ACCENT_BAR[accentTone] || null;
  const interactive = typeof onOpen === 'function';

  const open = (event) => {
    if (!interactive) return;
    if (event.target.closest('[data-row-action]')) return;
    onOpen();
  };

  const onKeyDown = (event) => {
    if (!interactive) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpen();
    }
  };

  return (
    <article
      tabIndex={interactive ? 0 : undefined}
      onClick={open}
      onKeyDown={onKeyDown}
      className={`group relative overflow-hidden rounded-2xl ${schoolSurface.card} px-4 py-3 transition ${schoolSurface.hover} ${
        interactive ? 'cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400' : ''
      }`}
    >
      {accentClass && <span className={`absolute inset-y-0 left-0 w-0.5 ${accentClass}`} aria-hidden />}
      <div className={`flex items-center gap-3 ${accentClass ? 'pl-1' : ''}`}>
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/[0.04] ${iconClass}`}>
          <span className="material-icons text-[20px]">{icon}</span>
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-white" title={name}>
            {name}
          </p>
          <p
            className="mt-0.5 truncate font-mono text-[12px] tabular-nums text-muted-500"
            title={[address, extra].filter(Boolean).join(' · ')}
          >
            {address}
            {extra ? <span className="font-sans"> · {extra}</span> : null}
          </p>
        </div>
        {status}
        {(hoverActions || reserveActions) && (
          <div
            data-row-action
            className="flex h-8 min-w-8 shrink-0 items-center justify-center opacity-100 transition md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100"
          >
            {hoverActions}
          </div>
        )}
      </div>
      {children}
    </article>
  );
};

const iconActionTones = {
  muted: 'text-muted-400 hover:bg-white/[0.06] hover:text-white',
  critical: 'text-muted-500 hover:bg-critical/10 hover:text-critical',
  ok: 'text-positive hover:bg-positive/10',
  brand: 'bg-brand-400/10 text-brand-200 hover:bg-brand-400/20 hover:text-white',
  cyan: 'bg-accent-cyan/10 text-accent-cyan hover:bg-accent-cyan/20',
};

export const IconAction = ({ title, onClick, busy, icon, tone = 'muted', className = '' }) => {
  const colors = iconActionTones[tone] || iconActionTones.muted;

  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={busy}
      className={`inline-flex h-8 w-8 items-center justify-center rounded-lg transition disabled:opacity-50 ${colors} ${className}`}
    >
      <span className={`material-icons text-[18px] ${busy ? 'animate-spin' : ''}`}>
        {busy ? 'refresh' : icon}
      </span>
    </button>
  );
};
